import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import usePermissions from '../../../../../../../hooks/usePermissions'
import { useGetApprovalOptions } from '../hooks'

import { openForm } from '../../../../../../../modules/actions/forms'

import { isUserControllerSelector } from '../../../../../../../modules/selectors/app'

import { insertIf } from '../../../../../../../helpers/common'
import { ADD_COMMENTS_TO_UPLOADED_FILES } from '../../../../../../../constants/forms'
import { MEDIA_ORDER_FILES_PERMISSIONS } from '../../../../../../../constants/permissions'
import { APPROVED } from '../../../../../../../features/components/UploadedFilesField/helpers'

export const useGetDropdownOptions = ({
  handleDelete,
  openUploadFileForm,
  handleFileDownload,
  handleShowDisapprovalModal,
  handleStatusChange
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isUserController = useSelector(isUserControllerSelector)

  const permissions = usePermissions()
  const hasApprovePermissions = permissions && permissions.can('manage', MEDIA_ORDER_FILES_PERMISSIONS)

  const openAddCommentsToUploadedFilesForm = useCallback(
    (fileId, mediaId) => () => {
      dispatch(
        openForm({
          id: fileId,
          formName: ADD_COMMENTS_TO_UPLOADED_FILES,
          otherParams: `mediaId=${mediaId}`
        })
      )
    },
    [dispatch]
  )

  const getApprovalOptions = useGetApprovalOptions({ handleShowDisapprovalModal, handleStatusChange })

  return useCallback(
    ({ requestedFile, requestedFileRowId, mediaId, uploadedFile, isFloatedFile }) => {
      const requestedFileId = requestedFile?.id

      const showDelete = isFloatedFile || uploadedFile.approval_status !== APPROVED

      return [
        ...insertIf(showDelete, {
          text: t(isFloatedFile ? 'Remove file' : 'Replace file'),
          onClickHandler: handleDelete({ requestedFileRowId, fileId: uploadedFile.id, bookedMediaId: mediaId })
        }),
        ...insertIf(!isFloatedFile, {
          text: t('Upload another file'),
          onClickHandler: openUploadFileForm({
            requestedFileId: requestedFileId,
            mediaId,
            customDate: requestedFile?.custom_date && requestedFile.date_start
          })
        }),
        {
          text: t('Download file'),
          onClickHandler: handleFileDownload(uploadedFile)
        },
        ...insertIf(!isFloatedFile && hasApprovePermissions, ...getApprovalOptions({ uploadedFile, requestedFileId })),
        ...insertIf(!isFloatedFile && isUserController, {
          text: !!uploadedFile.comment ? t('Edit comment') : t('Add comment'),
          onClickHandler: openAddCommentsToUploadedFilesForm(uploadedFile.id, mediaId)
        })
      ]
    },
    [
      t,
      handleDelete,
      openUploadFileForm,
      handleFileDownload,
      hasApprovePermissions,
      getApprovalOptions,
      openAddCommentsToUploadedFilesForm,
      isUserController
    ]
  )
}
